import { DecimalPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core'
import {
  MixingEvent,
  SpeedPatternStage,
} from 'projects/commons/src/lib/types/mixing'
import { MixingComponent } from 'projects/components/src/lib/mixing/mixing.component'
import { AppService, MixingData } from './app.service'
import { GoogleMapsModule } from '@angular/google-maps'
import { CycDatePipe } from 'projects/tools/src/lib/cyc-date.pipe'
import { LocaleService } from 'projects/commons/src/lib/services/locale.service'
import { TimePipe } from 'projects/tools/src/lib/time.pipe'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { Amplify } from 'aws-amplify'
import { environment } from '../environments/environment'
import { concat, from, interval, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  filter,
  first,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators'
import { NewVersionCheckerService } from 'projects/commons/src/lib/services/new-version-checker.service'

type ParamStatus = { name: string; status: 'ok' | 'missing' }

const demoDrumSpeed = [11, 9, 6, 0, 11, 9, 0]
const demoDatas: MixingEvent[] = [
  // Mixing that ends in success
  {
    status: 'in_progress',
    lastUpdateTimestamp: new Date().toISOString(),
    currentStageIndex: 0,
    speedPattern: [
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 12,
        rpmMin: 10,
        rpmMax: 14,
        startTimestamp: '',
        status: 'in_progress',
      } as SpeedPatternStage,
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 6,
        rpmMin: 5,
        rpmMax: 7,
        startTimestamp: '',
        status: 'inactive',
      } as SpeedPatternStage,
    ],
  },
  {
    status: 'in_progress',
    lastUpdateTimestamp: new Date().toISOString(),
    currentStageIndex: 1,
    speedPattern: [
      {
        secondsTarget: 10,
        secondsElapsed: 10,
        rmpTarget: 12,
        rpmMin: 10,
        rpmMax: 14,
        startTimestamp: '',
        status: 'complete',
      } as SpeedPatternStage,
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 6,
        rpmMin: 5,
        rpmMax: 7,
        startTimestamp: '',
        status: 'preparing',
      } as SpeedPatternStage,
    ],
  },
  {
    status: 'in_progress',
    lastUpdateTimestamp: new Date().toISOString(),
    currentStageIndex: 1,
    speedPattern: [
      {
        secondsTarget: 10,
        secondsElapsed: 10,
        rmpTarget: 12,
        rpmMin: 10,
        rpmMax: 14,
        startTimestamp: '',
        status: 'complete',
      } as SpeedPatternStage,
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 6,
        rpmMin: 5,
        rpmMax: 7,
        startTimestamp: '',
        status: 'in_progress',
      } as SpeedPatternStage,
    ],
  },
  {
    status: 'success',
    lastUpdateTimestamp: new Date().toISOString(),
    currentStageIndex: -1,
    speedPattern: [
      {
        secondsTarget: 10,
        secondsElapsed: 10,
        rmpTarget: 12,
        rpmMin: 10,
        rpmMax: 14,
        startTimestamp: '',
        status: 'complete',
      } as SpeedPatternStage,
      {
        secondsTarget: 20,
        secondsElapsed: 0,
        rmpTarget: 6,
        rpmMin: 5,
        rpmMax: 7,
        startTimestamp: '',
        status: 'complete',
      } as SpeedPatternStage,
    ],
  },
  // Mixing that is not completed
  {
    status: 'in_progress',
    lastUpdateTimestamp: new Date().toISOString(),
    currentStageIndex: 0,
    speedPattern: [
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 12,
        rpmMin: 10,
        rpmMax: 14,
        startTimestamp: '',
        status: 'in_progress',
      } as SpeedPatternStage,
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 6,
        rpmMin: 5,
        rpmMax: 7,
        startTimestamp: '',
        status: 'inactive',
      } as SpeedPatternStage,
    ],
  },
  {
    status: 'in_progress',
    lastUpdateTimestamp: new Date().toISOString(),
    currentStageIndex: 1,
    speedPattern: [
      {
        secondsTarget: 10,
        secondsElapsed: 10,
        rmpTarget: 12,
        rpmMin: 10,
        rpmMax: 14,
        startTimestamp: '',
        status: 'complete',
      } as SpeedPatternStage,
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 6,
        rpmMin: 5,
        rpmMax: 7,
        startTimestamp: '',
        status: 'preparing',
      } as SpeedPatternStage,
    ],
  },
  {
    status: 'fail',
    lastUpdateTimestamp: new Date().toISOString(),
    currentStageIndex: -1,
    speedPattern: [
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 12,
        rpmMin: 10,
        rpmMax: 14,
        startTimestamp: '',
        status: 'complete',
      } as SpeedPatternStage,
      {
        secondsTarget: 10,
        secondsElapsed: 0,
        rmpTarget: 6,
        rpmMin: 5,
        rpmMax: 7,
        startTimestamp: '',
        status: 'preparing',
      } as SpeedPatternStage,
    ],
  },
]

@Component({
  selector: 'app-root',
  imports: [MixingComponent, GoogleMapsModule],
  providers: [
    DecimalPipe,
    CycDatePipe,
    TimePipe,
    LocaleService,
    NewVersionCheckerService,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private appSvc = inject(AppService)
  private versionCheckerSvc = inject(NewVersionCheckerService)

  private _demoMode = signal(false)
  public demoMode = this._demoMode.asReadonly()

  public TruckSpeedPattern = signal<MixingData | null>(null)
  public errors = signal<string[]>([])

  private _params = new Subject<{
    rmc: string
    plant: string
    loadingPoint: string
  }>()
  private params$ = this._params.asObservable().pipe(
    filter((params) => !!params.rmc && !!params.plant && !!params.loadingPoint),
    shareReplay(1),
  )

  public params = toSignal(this.params$)

  public missingParams = signal<string[]>([])

  public mixingData$: Observable<MixingData | null> = this.params$.pipe(
    switchMap((params) => {
      const initSpeedPattern$ = from(
        this.appSvc.latestBatchSpeedPattern(
          params.rmc,
          params.plant,
          params.loadingPoint,
        ),
      ).pipe(
        catchError((err) => {
          console.log('error getting latestBatchSpeedPattern', err)
          this.errors.set([
            ...this.errors(),
            'Error getting latest Mixing details',
          ])
          return of(null)
        }),
        first(),
        tap((update) => console.log('initSpeedPattern', update)),
      )

      const updates$ = this.appSvc
        .loadingPointSpeedPatternUpdates(
          params.rmc,
          params.plant,
          params.loadingPoint,
        )
        .pipe(tap((update) => console.log('update', update)))

      const result$ = concat(initSpeedPattern$, updates$)
      return result$
    }),
  )

  public mixingData = toSignal(this.mixingData$)

  private batchPlantName$ = this.params$.pipe(
    switchMap(async (params) => {
      const batchPlant = await this.appSvc.getBatchPlant(
        params.rmc,
        params.plant,
      )
      return batchPlant ? batchPlant.name : params.plant
    }),
    shareReplay(1),
  )
  public batchPlantName = toSignal(this.batchPlantName$)

  title = 'batchMixing'

  public drumSpeed = signal(11)
  public demoData = signal<MixingEvent | null>(null)

  constructor() {
    // check for update on app start
    this.versionCheckerSvc.checkForUpdate()
    // check for updates every 15 minutes
    interval(1000 * 60 * 15)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.versionCheckerSvc.checkForUpdate()
      })
  }

  ngOnInit(): void {
    const params = new URLSearchParams(window.location.search)
    const apiKey = params.get('apikey')
    const rmc = params.get('rmc')
    const plant = params.get('plant')
    const loadingPoint = params.get('loadingpoint')
    const demo = params.get('demo')
    if (demo) {
      let demoVal = parseInt(demo, 10)
      // assume it's a number, no updates to new states
      let withUpdates = false
      if (isNaN(demoVal)) {
        // no specified demo value, start at 0 with updates
        demoVal = 0
        withUpdates = true
      }
      this.updateDemoData(demoVal, withUpdates)
      this._demoMode.set(true)
      return
    }
    // check for missing params
    if (!apiKey || !rmc || !plant || !loadingPoint) {
      const missingParams: string[] = []
      if (!apiKey) {
        missingParams.push('apikey')
      }
      if (!rmc) {
        missingParams.push('rmc')
      }
      if (!plant) {
        missingParams.push('plant')
      }
      if (!loadingPoint) {
        missingParams.push('loadingpoint')
      }
      this.missingParams.set(missingParams)
    }

    // setup amplify
    if (rmc && plant && loadingPoint) {
      Amplify.configure({
        API: {
          GraphQL: {
            endpoint: environment.graphqlEndpoint,
            region: environment.userPoolId.split('_')[0],
            defaultAuthMode: 'apiKey',
            apiKey: apiKey as string,
          },
        },
      })
      this._params.next({ rmc, plant, loadingPoint })
    }
  }

  private updateDemoData(demoIdx: number, withUpdates: boolean) {
    // keep demoIdx value if it = demoDatas.length
    // this will set demoData to null which is "No Event, waiting for next truck"
    // next iteration will set demoIdx to 0 and start the loop again
    if (demoIdx < 0 || demoIdx > demoDatas.length) {
      demoIdx = 0
    }
    const data = demoDatas[demoIdx] || null
    if (data) {
      data.lastUpdateTimestamp = new Date().toISOString()
    }
    this.demoData.set(data)
    this.drumSpeed.set(demoDrumSpeed[demoIdx] || 0)
    if (withUpdates) {
      setTimeout(() => this.updateDemoData(demoIdx + 1, true), 10000)
    }
  }
}
